import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { LexileDetailsAveragesDataItem } from 'teacher-ui-client/components/giraffe/charts/lexile-details/averages'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import fillMonths from 'teacher-ui-client/utils/student-events/fill-months'

export interface LexileDetailsAveragesResponse {
  lexile_averages_summary_by_grade: SummaryData[]
  lexile_averages_by_grade_by_month: AveragesData[]
}

interface SummaryData {
  grade_position: number
  data: [
    {
      category: 'student_count'
      count: number
    },
    {
      category: 'lexile_gain'
      count: number
    },
    {
      category: 'class_average'
      count: number
    },
  ]
}

interface AveragesData {
  grade_position: number
  months: {
    month: string
    student_count: number
    lexile_average: number
  }[]
}

export async function lexileDetailsAverages(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<LexileDetailsAveragesDataItem[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('lexile_averages_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<LexileDetailsAveragesResponse>(url, query)
  if (!data) return null

  const { lexile_averages_summary_by_grade: summaryData, lexile_averages_by_grade_by_month: averageData } = data
  if (summaryData.length === 0 || averageData.length === 0) return null

  return groupByGrade(summaryData, averageData, period)
}

function groupByGrade(
  summaryData: SummaryData[],
  averagesData: AveragesData[],
  period: NormalizedInterval,
): LexileDetailsAveragesDataItem[] {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const averages = averagesData.find((average) => average.grade_position === summary.grade_position)
      if (!averages) return null

      const student_count = summary.data.find((value) => value.category === 'student_count')!.count
      const lexile_gain = summary.data.find((value) => value.category === 'lexile_gain')!.count
      const class_average = summary.data.find((value) => value.category === 'class_average')!.count
      const filledMonths = fillMonths(averages.months, period, {
        student_count: 0,
        lexile_average: null,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          student_count,
          lexile_gain,
          class_average,
        },
        months: filledMonths,
      }
    })
    .filter((data) => data !== null)
}
